import React from 'react'
import { useGetBusiness } from '@marketing-milk/frontend'
import { SurfacePalette } from '../../tokens'
import { useSprint } from 'hooks/api/useSprint'
import { AdCreative } from '@marketing-milk/interfaces'
import { creativeWizardUrl } from '../../../creatives/creativeUrl'
import { SectionCard } from './SectionCard'
import { CreativeCardList } from './CreativeCardList'
import { useCreativeApproval } from './useCreativeApproval'
import { useCreativePreview } from './useCreativePreview'

interface Props {
  palette: SurfacePalette
  packetID: number
  sprintID?: number
}

/**
 * Edit-mode Creative section: the section chrome keeps its packet-local
 * `section` anchor (`<packetID>:creative`), while each rendered creative card
 * carries its own `creative` anchor (see `CreativeCardList`). Any pre-published
 * creative that isn't already Approved exposes an Approve action that records an
 * AE-attested client approval; Approved creatives expose a Remove-approval action.
 * Both flow through the shared creative review system.
 */
export const CreativeSection = ({ palette, packetID, sprintID }: Props) => {
  const business = useGetBusiness()
  const { sprintCreatives, sprintCreativesLoading } = useSprint(sprintID)
  const { onApprove, onRemoveApproval, modals } = useCreativeApproval(business.id, palette)

  // Edit-only: source edits (typos, copy) belong at the creative, not the packet.
  const onOpenSource = (creative: AdCreative) =>
    window.open(creativeWizardUrl(business.id, creative.id), '_blank', 'noopener')

  // Approve / Remove / Open-source live on the preview (the only surface showing
  // the full ad copy), not on the card.
  const {
    onPreview,
    prefetch,
    modals: previewModals,
  } = useCreativePreview(palette, undefined, {
    onApprove,
    onRemoveApproval,
    onOpenSource,
  })

  return (
    <SectionCard
      palette={palette}
      sectionKey="creative"
      title="Creative"
      commentAnchor={{ type: 'section', id: `${packetID}:creative`, label: 'Creative' }}
    >
      <CreativeCardList
        palette={palette}
        creatives={sprintCreatives}
        loading={sprintCreativesLoading}
        noSprint={!sprintID}
        onPreview={onPreview}
        onPrefetch={prefetch}
      />
      {modals}
      {previewModals}
    </SectionCard>
  )
}
